// Display formatting — TODO T17 (30 Aug 2026).
//
// Every timestamp the BFF returns is an ISO string in UTC. These turn them into what a recruiter
// reads, and they are pure functions taking `now` so the suite is not a clock race.

/** A link a record keeps as typed, as a browser can open it: "linkedin.com/in/x" gets its https://. */
export const externalHref = (href: string) =>
  /^https?:\/\//i.test(href) ? href : `https://${href}`;

/** A link as a row shows it, without the scheme or the www: "linkedin.com/in/x". */
export const linkLabel = (href: string) => href.replace(/^https?:\/\/(www\.)?/i, '');

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "just now" / "6m ago" / "3h ago" / "2d ago".
 *
 * The brain's freshness indicator, and the honest one: the screen is as current as the last
 * poll, not as current as the database (CLAUDE.md § Brain tier, "polling not sockets").
 */
export function formatRelative(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '-';
  const elapsed = now.getTime() - new Date(iso).getTime();
  if (Number.isNaN(elapsed)) return '-';
  if (elapsed < 0) return 'just now';
  if (elapsed < MINUTE) return 'just now';
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m ago`;
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)}h ago`;
  return `${Math.floor(elapsed / DAY)}d ago`;
}

/**
 * "30 Aug 2026, 11:05" — in the reader's own time zone. Until 15 Sep 2026 every time in the brain
 * was UTC, so a noon meeting in Stockholm read 10:00 (Natanel: "the timings and dates they say
 * are wrong"). en-GB either way; `timeZone` is for the suite, which pins one.
 */
export function formatDateTime(iso: string | null, timeZone?: string): string {
  if (!iso) return '-';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '-';
  return at.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
}

/**
 * "30 Aug 2026". A moment is dated in the reader's zone, as `formatDateTime` times it; a value at
 * UTC midnight is a DATE — a Search's deadline is stored so — and is read in UTC, or everyone
 * west of London would see the day before.
 */
export function formatDate(iso: string | null, timeZone?: string): string {
  if (!iso) return '-';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '-';
  const zone = at.getTime() % DAY === 0 ? 'UTC' : timeZone;
  return at.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(zone ? { timeZone: zone } : {}),
  });
}

/** Two letters for an avatar. Falls back to the E.164's last two digits when there is no name. */
export function initials(name: string | null, fallback = '??'): string {
  if (!name) return fallback;
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  const first = parts[0]![0] ?? '';
  const last = parts.length > 1 ? (parts.at(-1)![0] ?? '') : (parts[0]![1] ?? '');
  return (first + last).toUpperCase();
}

/**
 * Seconds-granularity, for the top bar's freshness indicator: "6s ago".
 *
 * The brain shows how old its data is because it polls rather than streams — the design of
 * record calls that out explicitly ("'Updated 6s ago' in the top bar reflects polling, not
 * sockets"). A recruiter deciding whether to act on a thread should be able to see that.
 */
export function formatAgo(at: number | null, now: number = Date.now()): string {
  if (at === null || at <= 0) return 'never';
  const elapsed = Math.max(0, now - at);
  if (elapsed < MINUTE) return `${Math.floor(elapsed / 1000)}s ago`;
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m ago`;
  return `${Math.floor(elapsed / HOUR)}h ago`;
}

/**
 * A list item's reminder time (T328a, 12 Oct 2026) is read and told in Israel time, as the
 * WhatsApp words read it (services/bff/src/when.ts `WHEN_ZONE`), whatever zone the browser is in.
 */
export const REMINDER_ZONE = 'Asia/Jerusalem';

const wallParts = (at: Date, zone: string): Record<string, string> => {
  const parts: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at))
    parts[part.type] = part.value;
  return parts;
};

/** "Fri 16 Oct, 09:00" in the reminder zone, as the WhatsApp list shows it. */
export function formatReminder(
  iso: string,
  zone: string = REMINDER_ZONE,
  now: Date = new Date(),
): string {
  const p = wallParts(new Date(iso), zone);
  const thisYear = wallParts(now, zone).year === p.year;
  return `${p.weekday} ${p.day} ${p.month}${thisYear ? '' : ` ${p.year}`}, ${p.hour}:${p.minute}`;
}

/** The `YYYY-MM-DDTHH:mm` a datetime-local box wants, for an instant, on the zone's wall clock. */
export function reminderWall(iso: string, zone: string = REMINDER_ZONE): string {
  const at = new Date(iso);
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at);
  const get = (type: string) => p.find((part) => part.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/**
 * Where a reminder's time box opens (T328d): on the item's time while it is ahead, else — no time yet,
 * or one already past — on the next whole hour, a time that can be set as it stands.
 */
export function reminderBoxStart(
  remindAt: string | null,
  now: Date = new Date(),
  zone: string = REMINDER_ZONE,
): string {
  if (remindAt !== null && new Date(remindAt).getTime() > now.getTime()) {
    return reminderWall(remindAt, zone);
  }
  const hour = 60 * 60_000;
  return reminderWall(new Date((Math.floor(now.getTime() / hour) + 1) * hour).toISOString(), zone);
}

/** The instant a datetime-local box's wall time is in the zone, or null for an unreadable one. */
export function reminderInstant(wall: string, zone: string = REMINDER_ZONE): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/u.exec(wall);
  if (!m) return null;
  const asUtc = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!);
  // The zone's offset at that moment, found by reading the guess back on the zone's clock; a second
  // pass settles a wall time that sits across a change of clocks.
  let guess = asUtc;
  for (let pass = 0; pass < 2; pass += 1) {
    const back = reminderWall(new Date(guess).toISOString(), zone);
    const [d, t] = back.split('T');
    const [y, mo, da] = d!.split('-').map(Number);
    const [h, mi] = t!.split(':').map(Number);
    guess += asUtc - Date.UTC(y!, mo! - 1, da!, h!, mi!);
  }
  return new Date(guess).toISOString();
}
